import React, { useState, useEffect, useCallback, useRef } from "react";
import { css } from "@emotion/css";
import { useHistory } from "react-router-dom";
import { Button, Grid, Icon, Menu, Segment, Sidebar, Transition } from "semantic-ui-react";
import { isValidPermission, isWhiteLabeled, setBackgroundColor, guid } from "../Helpers";
import "../styles/main.scss";
import "../styles/sidebar.scss";
import BCModal from "./ui/BCModal";
import { GlobalConstants, Personas } from "./constants/GlobalConstants";
import { getIsWhiteLabeled, whoAmI } from "../helpers/PermissionHelper";
import { useLMSGlobalReducer } from "./lms/LmsGlobalStore";
import { FeatureFlags } from "./constants/LocalServerConstants";

const BCSidebar = ({
  isGreaterThanMobile,
  isGreaterThanTablet,
  isSidebar,
  setIsSidebar,
  setIsSidebarToggle,
  user,
  proxyUser,
  profile,
  isProfileLoading,
  setIsModalOpen,
  children,
  permissions,
  isCreateNewUser,
  setIsCreateNewUser,
  brokerColor,
  isUserProfileEdit,
  isUserProfileUpdate
}) => {
  const [activeMenu, setActiveMenu] = useState("" as string);
  const [lastClickedMenu, setLastClickedMenu] = useState("" as string);
  const [activeSubmenu, setActiveSubmenu] = useState("" as string);
  const [submenuHover, setSubmenuHover] = useState("" as string);
  const [timer, setTimer] = useState(null as NodeJS.Timer | null);
  const [targetLocation, setTargetLocation] = useState("/" as string);
  const [isProfileConfirmationModal, setIsProfileConfirmationModal] = useState(false as boolean);
  const [{ activeTab }, dispatch] = useLMSGlobalReducer();
  const unblockRef = useRef<any>();
  const persona = whoAmI(user, proxyUser);
  const isGuarantorPersona = [
    Personas.NWL_GUARANTOR,
    Personas.WL_GUARANTOR
  ].indexOf(persona) > -1;
  
  const activeProfile = (proxyUser && proxyUser.id) ? proxyUser : profile;
  const isServicingUser = (isGuarantorPersona && activeProfile?.hasClosedLoans);
  const portals = [
    {
      portal: "Broker",
      portals: ["Broker"],
      menus: [
        {
          menu: "pricing",
          routes: ["pricing", "summary", "getmyrate"]
        },
        {
          menu: "applications",
          routes: ["applications", "application/borrower", "application/pricing"]
        },
        {
          menu: "loans",
          routes: ["loans", "processing"]
        },
        {
          menu: "servicing",
          routes: ["servicing"]
        },
        {
          menu: "paidachpayments",
          routes: ["paidachpayments"],
          menugroup: "servicing"
        },
        {
          menu: "pendingapproval",
          routes: ["pendingapproval"],
          menugroup: "servicing"
        },
        {
          menu: "vendor",
          routes: ["vendor"],
          menugroup: "servicing"
        },
        {
          menu: "faqs",
          routes: ["faqs"],
          menugroup: "servicing"
        },
        {
          menu: "payoffquoterequest",
          routes: ["payoffquoterequest"],
          menugroup: "servicing"
        },
        {
          menu: "payoffquoterequestauto",
          routes: ["payoffquoterequestauto"],
          menugroup: "servicing"
        },
        {
          menu: "makeapayment",
          routes: ["makeapayment"],
          menugroup: "servicing"
        },
        {
          menu: "projects",
          routes: ["projects"],
          menugroup: "servicing"
        },
        {
          menu: "currentinspections",
          routes: ["currentinspections"],
          menugroup: "servicing"
        },
        {
          menu: "requestedinspections",
          routes: ["requestedinspections"],
          menugroup: "servicing"
        },
        {
          menu: "pastinspections",
          routes: ["pastinspections"],
          menugroup: "servicing"
        },
        // {
        //   menu: "calendar",
        //   routes: ["calendar"]
        // }
      ]
    },
    {
      portal: "Loan Officer",
      portals: ["Loan Officer"],
      menus: [
        {
          menu: "deals",
          routes: ["deals", "deal"]
        },
        {
          menu: "activities",
          routes: ["activities"]
        }
      ]
    },
    {
      portal: "LMS",
      portals: ["LMS"],
      menus: [
        {
          menu: "loanapplications",
          routes: ["loanapplications", "loanapplication"],
          menugroup: "loans"
        },
        {
          menu: "loandetails",
          routes: ["loandetails"],
          menugroup: "loans"
        },
        {
          menu: "loanuploadbudget",
          routes: ["loanuploadbudget"],
          menugroup: "loans"
        },
        {
          menu: "loanassignments",
          routes: ["loanassignments"],
          menugroup: "loans"
        },
        {
          menu: "sales-team-national-sales-manager",
          routes: ["sales-team-national-sales-manager"],
          menugroup: "sales-team"
        },
        {
          menu: "sales-team-regional-sales-manager",
          routes: ["sales-team-regional-sales-manager"],
          menugroup: "sales-team"
        },
        {
          menu: "sales-team-senior-sales-executives",
          routes: ["sales-team-senior-sales-executives"],
          menugroup: "sales-team"
        },
        {
          menu: "sales-team-loan-officer-sales-manager",
          routes: ["sales-team-loan-officer-sales-manager"],
          menugroup: "sales-team"
        },
        {
          menu: "sales-team-loan-officer-portfolio-manager",
          routes: ["sales-team-loan-officer-portfolio-manager"],
          menugroup: "sales-team"
        },
        {
          menu: "sales-team-region",
          routes: ["sales-team-region"],
          menugroup: "sales-team"
        },
        {
          menu: "organizations",
          routes: ["organizations"],
          menugroup: "organizations"
        },
        {
          menu: "organizations-borrowers",
          routes: ["organizations-borrowers"],
          menugroup: "organizations"
        },
        {
          menu: "organizations-contacts",
          routes: ["organizations-contacts"],
          menugroup: "organizations"
        },
        {
          menu: "organizations-merge-contacts",
          routes: ["organizations-merge-contacts"],
          menugroup: "organizations"
        },
        {
          menu: "organizations-borrower-org-comments",
          routes: ["organizations-borrower-org-comments"],
          menugroup: "organizations"
        },
        {
          menu: "organizations-bank-accounts",
          routes: ["organizations-bank-accounts"],
          menugroup: "organizations"
        },
        {
          menu: "servicing-draws",
          routes: ["servicing-draws"],
          menugroup: "servicing"
        },
        {
          menu: "servicing-auto-draws",
          routes: ["servicing-auto-draws"],
          menugroup: "servicing"
        },
        {
          menu: "servicing-draw-management",
          routes: ["servicing-draw-management"],
          menugroup: "servicing"
        },
        {
          menu: "servicing-btb-loans-for-approval",
          routes: ["servicing-btb-loans-for-approval"],
          menugroup: "servicing"
        },
        {
          menu: "servicing-payoff-quotes",
          routes: ["servicing-payoff-quotes"],
          menugroup: "servicing"
        },
        {
          menu: "servicing-payoff-transactions",
          routes: ["servicing-payoff-transactions"],
          menugroup: "servicing"
        },
        {
          menu: "servicing-purchase-company-transactions",
          routes: ["servicing-purchase-company-transactions"],
          menugroup: "servicing"
        },
        {
          menu: "servicing-purchase-advice-transactions",
          routes: ["servicing-purchase-advice-transactions"],
          menugroup: "servicing"
        },
        {
          menu: "servicing-request-inspections",
          routes: ["servicing-request-inspections"],
          menugroup: "servicing"
        },
        {
          menu: "servicing-requested-inspections",
          routes: ["servicing-requested-inspections"],
          menugroup: "servicing"
        },
        {
          menu: "servicing-insurance",
          routes: ["servicing-insurance"],
          menugroup: "servicing"
        },
        {
          menu: "servicing-modify-budgets",
          routes: ["servicing-modify-budgets"],
          menugroup: "servicing"
        },
        {
          menu: "servicing-properties",
          routes: ["servicing-properties"],
          menugroup: "servicing"
        },
        {
          menu: "servicing-inspection-company-messages",
          routes: ["servicing-inspection-company-messages"],
          menugroup: "servicing"
        },
        {
          menu: "servicing-units",
          routes: ["servicing-units"],
          menugroup: "servicing"
        },
        {
          menu: "servicing-loan-history",
          routes: ["servicing-loan-history"],
          menugroup: "servicing"
        },
        {
          menu: "servicing-loan-conditions",
          routes: ["servicing-loan-conditions"],
          menugroup: "servicing"
        },
        {
          menu: "servicing-current-inspections",
          routes: ["servicing-current-inspections"],
          menugroup: "servicing"
        },
        {
          menu: "servicing-auto-po-restrictions",
          routes: ["servicing-auto-po-restrictions"],
          menugroup: "servicing"
        },
        {
          menu: "servicing-close-accounting-month",
          routes: ["servicing-loan-history"],
          menugroup: "servicing"
        },
        {
          menu: "servicing-modify-btb-budgets",
          routes: ["servicing-modify-btb-budgets"],
          menugroup: "servicing"
        },
        {
          menu: "servicing-purchase-advice-quote",
          routes: ["servicing-purchase-advice-quote"],
          menugroup: "servicing"
        },
        {
          menu: "servicing-loan-sale-approvals",
          routes: ["servicing-loan-sale-approvals"],
          menugroup: "servicing" 
        },
        {
          menu: "servicing-amendments",
          routes: ["servicing-amendments"],
          menugroup: "servicing" 
        },
        {
          menu: "accounting-loan-transactions",
          routes: ["accounting-loan-transactions"],
          menugroup: "accounting"
        },
        {
          menu: "accounting-post-payments",
          routes: ["accounting-post-payments"],
          menugroup: "accounting"
        },
        {
          menu: "accounting-bank-routings",
          routes: ["accounting-bank-routings"],
          menugroup: "accounting"
        },
        {
          menu: "accounting-purchasing-company-interest",
          routes: ["accounting-purchasing-company-interest"],
          menugroup: "accounting"
        },
        {
          menu: "accounting-late-fee-transactions",
          routes: ["accounting-late-fee-transactions"],
          menugroup: "accounting"
        },
        {
          menu: "accounting-loan-draws-and-transactions",
          routes: ["accounting-loan-draws-and-transactions"],
          menugroup: "accounting"
        },
        {
          menu: "accounting-weekly-recalc-report",
          routes: ["accounting-weekly-recalc-report"],
          menugroup: "accounting"
        },
        {
          menu: "accounting-monthly-recalc-report",
          routes: ["accounting-monthly-recalc-report"],
          menugroup: "accounting"
        },
        {  
          menu: "accounting-ir-details",
          routes: ["accounting-ir-details"],
          menugroup: "accounting"
        },
        {
          menu: "accounting-net-suit-je",
          routes: ["accounting-net-suit-je"],
          menugroup: "accounting"
        },
        {
          menu: "accounting-ach-queue",
          routes: ["accounting-ach-queue"],
          menugroup: "accounting"
        },
        {
          menu: "accounting-ach-paid",
          routes: ["accounting-ach-paid"],
          menugroup: "accounting"
        },
        {
          menu: "accounting-residual-amounts-queue",
          routes: ["accounting-residual-amounts-queue"],
          menugroup: "accounting"
        },
        {
          menu: "documents",
          routes: ["documents"],
          menugroup: "documents"
        },
        {
          menu: "documents-upload-direct-pay-form",
          routes: ["documents-upload-direct-pay-form"],
          menugroup: "documents"
        },
        {
          menu: "documents-upload-draw",
          routes: ["documents-upload-draw"],
          menugroup: "documents"
        },
        {
          menu: "documents-upload-draw-management",
          routes: ["documents-upload-draw-management"],
          menugroup: "documents"
        },
        {
          menu: "documents-upload-loan-data-cash-flow",
          routes: ["documents-upload-loan-data-cash-flow"],
          menugroup: "documents"
        },
        {
          menu: "documents-docusign-status",
          routes: ["documents-docusign-status"],
          menugroup: "documents"
        },
        {
          menu: "documents-docusign-master",
          routes: ["documents-docusign-master"],
          menugroup: "documents"
        },
        {
          menu: "documents-upload-property-units",
          routes: ["documents-upload-property-units"],
          menugroup: "documents"
        },
        {
          menu: "documents-docusign-sender-receiver",
          routes: ["documents-docusign-sender-receiver"],
          menugroup: "documents"
        },
        {
          menu: "documents-upload-gmr-lookup",
          routes: ["documents-upload-gmr-lookup"],
          menugroup: "documents"
        },
        {
          menu: "reports-lms-reports",
          routes: ["reports-lms-reports"],
          menugroup: "reports"
        },
        {
          menu: "reports-borrower-statement-previous-owing",
          routes: ["reports-borrower-statement-previous-owing"],
          menugroup: "reports"
        },
        {
          menu: "reports-borrower-statement-current-interest-owing",
          routes: ["reports-borrower-statement-current-interest-owing"],
          menugroup: "reports"
        },
        {
          menu: "reports-ab-data-at-sale",
          routes: ["reports-ab-data-at-sale"],
          menugroup: "reports"
        },
        {
          menu: "reports-ab-data-after-sale",
          routes: ["reports-ab-data-after-sale"],
          menugroup: "reports"
        },
        {
          menu: "reports-waterfall-summary",
          routes: ["reports-waterfall-summary"],
          menugroup: "reports"
        },
        {
          menu: "reports-collections",
          routes: ["reports-collections"],
          menugroup: "reports"
        },
        {
          menu: "reports-wire-amounts",
          routes: ["reports-wire-amounts"],
          menugroup: "reports"
        },       
        {
          menu: "reports-inspection-ready",
          routes: ["reports-inspection-ready"],
          menugroup: "reports"
        },
        {
          menu: "vendors-budget-categories",
          routes: ["vendors-budget-categories"],
          menugroup: "vendors"
        },
        {
          menu: "vendors-products-services",
          routes: ["vendors-products-services"],
          menugroup: "vendors"
        },       
        {
          menu: "vendors-bank-accounts",
          routes: ["vendors-bank-accounts"],
          menugroup: "vendors"
        },
        {  
          menu: "inspection-company-data-match",
          routes: ["inspection-company-data-match"],
          menugroup: "inspections"
        },
        {
          menu: "vendors-merge-vendors",
          routes: ["vendors-merge-vendors"],
          menugroup: "vendors"
        },
        { 
          menu: "inspections-submit-to-inspection-company",
          routes: ["inspections-submit-to-inspection-company"],
          menugroup: "inspections"
        },
        {  
          menu: "inspection-company-data-match",
          routes: ["inspection-company-data-match"],
          menugroup: "inspections"
        },
        {
          menu: "inspection-company-pending-projects",
          routes: ["inspection-company-pending-projects"],
          menugroup: "inspections"
        },       
        {  
          menu: "inspections-inspection-company-projects",
          routes: ["inspections-inspection-company-projects"],
          menugroup: "inspections"
        },
        {
          menu: "inspections-inspection-company-project-documents",
          routes: ["inspections-inspection-company-project-documents"],
          menugroup: "inspections"
        },
        {  
          menu: "inspections-inspection-company-inspection-details",
          routes: ["inspections-inspection-company-inspection-details"],
          menugroup: "inspections"
        },
        {  
          menu: "inspections-inspection-company-inspection-documents",
          routes: ["inspections-inspection-company-inspection-documents"],
          menugroup: "inspections"
        },
        {
          menu: "inspections-inspection-company-inspections",
          routes: ["inspections-inspection-company-inspections"],
          menugroup: "inspections"
        },
        {  
          menu: "inspections-inspection-company-message-subject",
          routes: ["inspections-inspection-company-message-subject"],
          menugroup: "inspections"
        },
        {
          menu: "inspection-company-draw-funded",
          routes: ["inspection-company-draw-funded"],
          menugroup: "inspections"
        }, 
        {
          menu: "reports-lms-reports",
          routes: ["reports-lms-reports"],
          menugroup: "reports"
        },
        {
          menu: "reports-borrower-statement-current-interest-owing",
          routes: ["reports-borrower-statement-current-interest-owing"],
          menugroup: "reports"
        },
        {
          menu: "reports-ab-data-at-sale",
          routes: ["reports-ab-data-at-sale"],
          menugroup: "reports"
        },
        {
          menu: "reports-borrower-statement-previous-owing",
          routes: ["reports-borrower-statement-previous-owing"],
          menugroup: "reports"
        },
        {
          menu: "reports-wire-amounts",
          routes: ["reports-wire-amounts"],
          menugroup: "reports"
        },
        {
          menu: "reports-waterfall-summary",
          routes: ["reports-waterfall-summary"],
          menugroup: "reports"
        },        
        {
          menu: "reports-inspection-ready",
          routes: ["reports-inspection-ready"],
          menugroup: "reports"
        },                     
        {
          menu: "inspections-inspection-company-projects",
          routes: ["inspections-inspection-company-projects"],
          menugroup: "inspections"
        },
        {
          menu: "reports-ab-data-at-sale",
          routes: ["reports-ab-data-at-sale"],
          menugroup: "reports"
        },        
        {
          menu: "reports-ab-data-after-sale",
          routes: ["reports-ab-data-after-sale"],
          menugroup: "reports"
        },
        {
          menu: "reports-collections",
          routes: ["reports-collections"],
          menugroup: "reports"
        },
        {
          menu: "reports-borrower-statement-current-interest-owing",
          routes: ["reports-borrower-statement-current-interest-owing"],
          menugroup: "reports"
        },
        {
          menu: "vendors-budget-categories",
          routes: ["vendors-budget-categories"],
          menugroup: "vendors"
        },
        {
          menu: "vendors-products-services",
          routes: ["vendors-products-services"],
          menugroup: "vendors"
        },
        {
          menu: "vendors-service-areas",
          routes: ["vendors-service-areas"],
          menugroup: "vendors"
        },
        {
          menu: "voting-assign-fund",
          routes: ["voting-assign-fund"],
          menugroup: "voting"
        },
        {
          menu: "voting-loan-review",
          routes: ["voting-loan-review"],
          menugroup: "voting"
        },
        {
          menu: "voting-fund-polling",
          routes: ["voting-fund-polling"],
          menugroup: "voting"
        },
        {
          menu: "voting-randomizer",
          routes: ["voting-randomizer"],
          menugroup: "voting"
        },
        {
          menu: "voting-amendment-review",
          routes: ["voting-amendment-review"],
          menugroup: "voting"
        },
        {
          menu: "voting-cac-members",
          routes: ["voting-cac-members"],
          menugroup: "voting"
        },
        {
          menu: "voting-btb-p2-review",
          routes: ["voting-btb-p2-review"],
          menugroup: "voting"
        },
        {
          menu: "systems-users",
          routes: ["systems-users"],
          menugroup: "systems"
        },
        {
          menu: "systems-external-users",
          routes: ["systems-external-users"],
          menugroup: "systems"
        },
        {
          menu: "systems-locked-loans",
          routes: ["systems-locked-loans"],
          menugroup: "systems"
        },
        {
          menu: "systems-loan-comments",
          routes: ["systems-loan-comments"],
          menugroup: "systems"
        },
        {
          menu: "systems-related-parties",
          routes: ["systems-related-parties"],
          menugroup: "systems"
        },
        {
          menu: "new-menus-borrower-application-entity",
          routes: ["new-menus-borrower-application-entity"],
          menugroup: "new-menus"
        },
        {
          menu: "new-menus-borrower-vendor",
          routes: ["new-menus-borrower-vendor"],
          menugroup: "new-menus"
        },
        {
          menu: "new-menus-cross-collateral",
          routes: ["new-menus-cross-collateral"],
          menugroup: "new-menus"
        },
        {
          menu: "new-menus-document-master-requirement",
          routes: ["new-menus-document-master-requirement"],
          menugroup: "new-menus"
        },
        {
          menu: "new-menus-email-box",
          routes: ["new-menus-email-box"],
          menugroup: "new-menus"
        },
        {
          menu: "new-menus-email-templates",
          routes: ["new-menus-email-templates"],
          menugroup: "new-menus"
        },
        {
          menu: "new-menus-comment-subject",
          routes: ["new-menus-comment-subject"],
          menugroup: "new-menus"
        },
        {
          menu: "new-menus-gmr-project-cost",
          routes: ["new-menus-gmr-project-cost"],
          menugroup: "new-menus"
        },
        {
          menu: "new-menus-loan-applications",
          routes: ["new-menus-loan-applications"],
          menugroup: "new-menus"
        },
        {
          menu: "new-menus-email-box",
          routes: ["new-menus-email-box"],
          menugroup: "new-menus"
        },
        {
          menu: "new-menus-loan-condition-default",
          routes: ["new-menus-loan-condition-default"],
          menugroup: "new-menus"
        },
        {
          menu: "new-menus-loan-doc-generation",
          routes: ["new-menus-loan-doc-generation"],
          menugroup: "new-menus"
        },        
        {
          menu: "new-menus-console-reports",
          routes: ["new-menus-console-reports"],
          menugroup: "new-menus"
        },
        {
          menu: "new-menus-credit-request",
          routes: ["new-menus-credit-request"],
          menugroup: "new-menus"
        },
        {
          menu: "new-menus-loan-draw-history",
          routes: ["new-menus-loan-draw-history"],
          menugroup: "new-menus"
        },
        {
          menu: "new-menus-invoice",
          routes: ["new-menus-invoice"],
          menugroup: "new-menus"
        },
        {
          menu: "new-menus-legal-text",
          routes: ["new-menus-legal-text"],
          menugroup: "new-menus"
        },
        {
          menu: "credit-request",
          routes: ["credit-request"],
          menugroup: "new-menus"
        },
        {
          menu: "new-menus-loan-draw-batch",
          routes: ["new-menus-loan-draw-batch"],
          menugroup: "new-menus"
        },
        {
          menu: "new-menus-email-tag",
          routes: ["new-menus-email-tag"],
          menugroup: "new-menus"
        },
        {
          menu: "new-menus-email-master",
          routes: ["new-menus-email-master"],
          menugroup: "new-menus"
        },
        {
          menu: "new-menus-validation-message",
          routes: ["new-menus-validation-message"],
          menugroup: "new-menus",
        },
        { 
          menu: "new-menus-notification",
          routes: ["new-menus-notification"],
          menugroup: "new-menus"
        },
        {
          menu: "bank-loc",
          routes: ["bank-loc"],
          menugroup: "funds"
        },
        {
          menu: "bank-loc-history",
          routes: ["bank-loc-history"],
          menugroup: "funds"
        },
        {
          menu: "fund-fees",
          routes: ["fund-fees"],
          menugroup: "funds"
        },
        {
          menu: "fund-transactions",
          routes: ["fund-transactions"],
          menugroup: "funds"
        },
        {
          menu: "investors",
          routes: ["investors"],
          menugroup: "funds"
        },
        {
          menu: "voting-members",
          routes: ["voting-members"],
          menugroup: "funds"
        },
        {
          menu: "fund-allocation",
          routes: ["fund-allocation"],
          menugroup: "funds"
        },
        {
          menu: "fund-capacity",
          routes: ["fund-capacity"],
          menugroup: "funds"
        },
        {
          menu: "fund-capacity-commitment",
          routes: ["fund-capacity-commitment"],
          menugroup: "funds"
        },
        {
          menu: "fund-managers",
          routes: ["fund-managers"],
          menugroup: "funds"
        },
        {
          menu: "fund-financial-status",
          routes: ["fund-financial-status"],
          menugroup: "funds"
        }
      ]
    },
  ] as any;

  const isAdminBrmLoBrokerRetailGuarantor = [
    Personas.SYSTEM_ADMIN,
    Personas.SALES_ADMIN,
    Personas.BROKER_ADMIN,
    Personas.LOAN_OFFICER,
    Personas.BRM,
    Personas.BROKER,
    Personas.NWL_GUARANTOR
  ].indexOf(persona) > -1;

  const brokerSidebarIconOverrideCSS = css`
    color:${brokerColor ? brokerColor : "black"} !important;
    font-size: 22px !important;
  `;

  const history = useHistory();
  const handleSubMenuOver = (menu) => {
    if (submenuHover !== menu) {
      setSubmenuHover(menu);
    }
    else if (submenuHover === menu) {
      setSubmenuHover("");
    }
  };

  const handleSubMenuOut = () => {
    if (timer) {
      clearTimeout(timer);
      setTimer(null);
    }
  };

  const handleMenuItemClick = (link, submenu = null, title = null, isSubmenu = false) => {
    let url = link;
    if (submenu) {
      url = submenu;
    }
    else {
      handleNavigation(link);
    }

    if (isSubmenu) {
      if (title) {
        const id = guid();
        const addTabFunc = () => {
          dispatch({
            type: GlobalConstants.LMSStoreReducerCases.ADD_FILTER_TAB,
            payload: { title, path: url, id }
          });

          handleNavigation(link, submenu);
        };

        if (activeTab?.tabHasUpdatedForm) {
          dispatch({
            type: GlobalConstants.LMSStoreReducerCases.SET_CONFIRM_OPTIONS,
            payload: { isOpen: true, func: addTabFunc }
          });
        }
        else {
          addTabFunc();
        }
      }
    }
    else {
      if (lastClickedMenu !== link) {
        setLastClickedMenu(link);
      }
      else {
        setLastClickedMenu("");
      }

      handleSubMenuOut();
    }
  };

  const handleNavigation = useCallback((link, submenu = null) => {
    let url = link;
    if (submenu) {
      url = submenu;
    }

    if (user.portal === GlobalConstants.Portals.LMS && !link) {
      history.push("/");
    }

    if (user.portal !== GlobalConstants.Portals.LMS || submenu) {
      history.push("/" + url);
    }
  }, [history]);

  const handleProfileDoesNotExist = useCallback(() => {
    setIsModalOpen(true);
    history.push("/profile");
  }, [history, setIsModalOpen]);

  const handleError = useCallback(() => {
    history.push("/error");
  }, [history]);

  const handleShowModal = (targetLocation) => {
    setTargetLocation(targetLocation);
    setIsProfileConfirmationModal(true);
  };

  function handleProfileModalConfirm() {
    if (unblockRef) {
      unblockRef.current();
    }

    setIsProfileConfirmationModal(false);
    handleNavigation(targetLocation);
  }

  useEffect(() => {
    let newActiveMenu = "" as string;
    let newActiveSubmenu = "" as string;
    let newActiveMenuGroup = "" as string;
    if (window.location.pathname === "/profile" && isUserProfileEdit) {
      if (isUserProfileUpdate && unblockRef && unblockRef.current) {
        unblockRef.current();
      }
      else {
        unblockRef.current = history.block((targetLocation: any) => {
          handleShowModal(targetLocation);
          return false;
        });
        return function () {
          unblockRef.current.current && unblockRef.current.current();
        };
      }
    }

    if (window.location.pathname === "/" && getIsWhiteLabeled()) {
      newActiveMenu = "applications";
    }
    else if (window.location.pathname === "/") {
      newActiveMenu = "";
    }
    else {
      portals.forEach(portal => {
        if (portal.portals.includes(user.portal)) {
          portal.menus.forEach(menu => {
            if (menu.menugroup) {
              menu.routes.forEach(route => {
                if (window.location.pathname.includes(route)) {
                  newActiveSubmenu = menu.menu;
                  newActiveMenuGroup = menu.menugroup;
                }
              });
            }
            else {
              menu.routes.forEach(route => {
                if (window.location.pathname.includes(route)) {
                  newActiveMenuGroup = menu.menu;
                }
              });
            }
            newActiveMenu = newActiveMenuGroup;
          });
        }
      });
    }

    if (activeMenu !== newActiveMenu) {
      setActiveMenu(newActiveMenu);
      setLastClickedMenu("");
    }

    if (activeSubmenu !== newActiveSubmenu) {
      setActiveSubmenu(newActiveSubmenu);
      setLastClickedMenu("");
    }

    if (!profile && !isProfileLoading && window.location.pathname !== "/error") {
      if ((!getIsWhiteLabeled() || window.location.hostname.includes("builders-capital.com"))) {
        handleProfileDoesNotExist();
      }
      else {
        handleError();
      }

      setIsSidebar(false);
      setIsSidebarToggle(false);
    }
    else if ((profile && isProfileLoading) || isCreateNewUser) {
      if (isGreaterThanMobile) {
        setIsSidebar(true);
      }

      setIsSidebarToggle(true);
      setIsCreateNewUser(false);
      setIsModalOpen(false);
    }
  }, [
    portals,
    user,
    activeMenu,
    activeSubmenu,
    handleProfileDoesNotExist,
    profile,
    isProfileLoading,
    setIsSidebar,
    setIsSidebarToggle,
    permissions,
    isCreateNewUser,
    setIsCreateNewUser,
    setIsModalOpen,
    handleError,
    isUserProfileEdit,
    isUserProfileUpdate,
    history,
    isGreaterThanMobile
  ]);

  useEffect(() => {
    if (!isGreaterThanMobile) {
      setIsSidebar(false);
    }
  }, [
    isGreaterThanMobile,
    activeMenu,
    activeSubmenu,
    setIsSidebar
  ]);

  const SidebarMenuItem = ({ ...props }) => {
    return (
      <Menu.Item
        className="sidebar-item"
        onClick={() => props.handleOnClick ? props.handleOnClick() : handleNavigation(props.link)}
        active={activeMenu === props.link}
      >
        <div>
          <Icon className={getIsWhiteLabeled() ? brokerSidebarIconOverrideCSS : "sidebar-item-icon"} name={props.icon} />
          <span className="sidebar-item-text">{props.title}</span>
        </div>
      </Menu.Item>
    );
  };

  const SidebarSidemenuContainer = ({ ...props }) => {
    return (
      <Menu.Item
        className="sidebar-item-container"
        onClick={() => handleSubMenuOver(props.link)}
        active={activeMenu === props.link}
      >
        <div className="sidebar-item-content-container">
          <Icon className="sidebar-item-icon" name={props.icon} />
          <span className="sidebar-item-text">{props.title}</span>
        </div>
      </Menu.Item>
    );
  };

  const SidebarSubmenuItem = ({ ...props }) => {
    return (
      <Menu.Item
        className="sidebar-item sidebar-sub-item"
        onClick={() => props.handleOnClick ? props.handleOnClick() : handleMenuItemClick(props.link, props.submenu, props.title, true)}
        active={activeSubmenu === props.submenu}
      >
        <div className="sidebar-sub-item-content-container">
          <Icon className="sidebar-sub-item-icon" name={props.icon} />
          <div className="sidebar-sub-item-text">{props.title}</div>
        </div>
      </Menu.Item>
    );
  };

  const isMenuExpanded = (menu: string) => {
    return (activeMenu === menu || submenuHover === menu) && lastClickedMenu !== menu;
  };

  return (
    <div className="sidebar-menu">
      <Sidebar.Pushable as={Segment} className={"sidebar-menu-pushable"}>
        <Sidebar
          as={Menu}
          className="sidebar-menu-container"
          animation={isGreaterThanTablet ? "uncover" : "overlay"}
          direction="left"
          icon='labeled'
          vertical
          visible={isSidebar}
          width='thin'
        >
          <BCModal
            isModalOpen={isProfileConfirmationModal}
            onClose={() => setIsProfileConfirmationModal(false)}
            onOpen={() => setIsProfileConfirmationModal(true)}
            size="tiny"
            header="Are you sure you want to leave?"
            content={<React.Fragment>
              <div className="welcome-text">Any changes will be lost.</div>
            </React.Fragment>}
            actions={<Grid>
              <Grid.Row>
                <Grid.Column width={8}>
                  <Button
                    fluid
                    onClick={() => setIsProfileConfirmationModal(false)}
                  >
                    <div className="gmr-button-container">
                      <div className="gmr-button-text">No</div>
                    </div>
                  </Button>
                </Grid.Column>
                <Grid.Column width={8}>
                  <Button
                    className="bp-button"
                    fluid
                    onClick={() => handleProfileModalConfirm()}
                    style={isWhiteLabeled(permissions) ? setBackgroundColor(brokerColor) : void 0}
                  >
                    <div className="gmr-button-container">
                      <div className="gmr-button-text">Yes</div>
                    </div>
                  </Button>
                </Grid.Column>
              </Grid.Row>
            </Grid>}
          />

          {user.portal === GlobalConstants.Portals.BROKER && <React.Fragment>
            {(isAdminBrmLoBrokerRetailGuarantor && !getIsWhiteLabeled()) && <React.Fragment>
              <SidebarMenuItem link="" icon="home" title="Home" />
              <SidebarMenuItem link="pricing" icon="dollar sign" title="Pricing" />
            </React.Fragment>}
            <SidebarMenuItem link="applications" icon="clipboard list" title="Applications" />
            <SidebarMenuItem link="loans" icon="list" title="Processing" />
            {(isServicingUser && FeatureFlags.IS_BORROWER_PORTAL) && <><SidebarSidemenuContainer link="servicing" icon="table" title="Servicing" />
            <Transition.Group
              animation={"fade down"}
              duration={500}
            >
              {(activeMenu === "servicing" || submenuHover === "servicing") && <div>
                <SidebarSubmenuItem link="servicing" submenu="paidachpayments" icon="dollar sign" title="Paid ACH Payment" />
                <SidebarSubmenuItem link="servicing" submenu="pendingapproval" icon="clock" title="Pending Approval" />
                <SidebarSubmenuItem link="servicing" submenu="vendor" icon="handshake outline" title="Vendor" />
                <SidebarSubmenuItem handleOnClick={() => window.open(GlobalConstants.Links.FAQ, "_blank")} icon="help circle" title="FAQs" />
                <SidebarSubmenuItem handleOnClick={() => window.open(GlobalConstants.Links.PAYOFF_REQUEST, "_blank")} icon="write square" title="Payoff Quote Request" />
                <SidebarSubmenuItem link="servicing" submenu="payoffquoterequestauto" icon="write square" title="Payoff Quote Request (Auto)" />
                <SidebarSubmenuItem link="servicing" submenu="makeapayment" icon="credit card outline" title="Make a Payment" />
                <SidebarSubmenuItem link="servicing" submenu="projects" icon="building" title="Projects" />
                <SidebarSubmenuItem link="servicing" submenu="currentinspections" icon="table" title="Current Inspections" />
                <SidebarSubmenuItem link="servicing" submenu="requestedinspections" icon="clipboard check" title="Requested Inspections" />
                <SidebarSubmenuItem link="servicing" submenu="pastinspections" icon="calendar check outline" title="Past inspections" />
              </div>}
            </Transition.Group></>}
            {/* {FeatureFlags.IS_GAME_CHANGERS && <SidebarMenuItem link="calendar" icon="calendar alternate" title="Calendar" />} */}
          </React.Fragment>}
          {user.portal === GlobalConstants.Portals.LOAN_OFFICER && <React.Fragment>
            <SidebarMenuItem link="" icon="home" title="Home" />
            <SidebarMenuItem link="deals" icon="clipboard list" title="Deals" />
            <SidebarMenuItem link="activities" icon="alarm" title="Activities" />
          </React.Fragment>}
          {user.portal === GlobalConstants.Portals.LMS && <React.Fragment>
            {isValidPermission(permissions, "LMSVersion2", "read") && <SidebarMenuItem link="" icon="home" title="Home" />}
            <SidebarSidemenuContainer link="loans" submenu="loanapplications" icon="table" title="Loans" />
            <Transition.Group
              animation={"fade down"}
              duration={500}
            >
              {isMenuExpanded("loans") && <div>
                <SidebarSubmenuItem link="loans" submenu="loanapplications" icon="clipboard outline" title="Applications" />
                <SidebarSubmenuItem link="loans" submenu="loandetails" icon="list" title="Details" />
                <SidebarSubmenuItem link="loans" submenu="loanbudgettemplates" icon="calculator" title="Budget Templates" />
                <SidebarSubmenuItem link="loans" submenu="loanassignments" icon="tasks" title="Assignments" />
                <SidebarSubmenuItem link="loans" submenu="loangmr" icon="tasks" title="GMR" />
              </div>}
            </Transition.Group>
            <SidebarSidemenuContainer link="sales-team" submenu="sales-team-national-sales-manager" icon="table" title="Sales Team" />
            <Transition.Group
              animation={"fade down"}
              duration={500}
            >
              {isMenuExpanded("sales-team") && <div>
                <SidebarSubmenuItem link="sales-team" submenu="sales-team-national-sales-manager" icon="clipboard outline" title="National Sales Manager" />
                <SidebarSubmenuItem link="sales-team" submenu="sales-team-regional-sales-manager" icon="clipboard outline" title="Regional Sales Manager" />
                <SidebarSubmenuItem link="sales-team" submenu="sales-team-senior-sales-executives" icon="clipboard outline" title="Senior Sales Executives" />
                <SidebarSubmenuItem link="sales-team" submenu="sales-team-loan-officer-sales-manager" icon="clipboard outline" title="Loan Officer - Sales Manager" />
                <SidebarSubmenuItem link="sales-team" submenu="sales-team-loan-officer-portfolio-manager" icon="clipboard outline" title="Loan Officer Portfolio Manager" />
                <SidebarSubmenuItem link="sales-team" submenu="sales-team-region" icon="clipboard outline" title="Region" />
              </div>}
            </Transition.Group>
            <SidebarSidemenuContainer link="organizations" icon="table" title="Organizations" />
            <Transition.Group
              animation={"fade down"}
              duration={500}
            >
              {isMenuExpanded("organizations") && <div>
                <SidebarSubmenuItem link="organizations" submenu="organizations" icon="clipboard outline" title="Organizations" />
                <SidebarSubmenuItem link="organizations" submenu="organizations-borrowers" icon="clipboard outline" title="Borrowers" />
                <SidebarSubmenuItem link="organizations" submenu="organizations-contacts" icon="clipboard outline" title="Contacts" />
                <SidebarSubmenuItem link="organizations" submenu="organizations-merge-contacts" icon="clipboard outline" title="Merge Contacts" />
                <SidebarSubmenuItem link="organizations" submenu="organizations-borrower-org-comments" icon="clipboard outline" title="Borrower/Org Comments" />
                <SidebarSubmenuItem link="organizations" submenu="organizations-bank-accounts" icon="clipboard outline" title="Bank Accounts" />
              </div>}
            </Transition.Group>
            <SidebarSidemenuContainer link="servicing" submenu="servicing-requested-inspections" icon="table" title="Servicing" />
            <Transition.Group
              animation={"fade down"}
              duration={500}
            >
              {isMenuExpanded("servicing") && <div>
                <SidebarSubmenuItem link="servicing" submenu="servicing-requested-inspections" icon="clipboard outline" title="Requested Inspections"/>
                <SidebarSubmenuItem link="servicing" submenu="servicing-draws" icon="clipboard outline" title="Draws"/>
                <SidebarSubmenuItem link="servicing" submenu="servicing-auto-draws" icon="clipboard outline" title="Auto Draws"/>
                <SidebarSubmenuItem link="servicing" submenu="servicing-request-inspections" icon="clipboard outline" title="Request Inspections"/>
                <SidebarSubmenuItem link="servicing" submenu="servicing-draw-management" icon="clipboard outline" title="Draw Management"/>
                <SidebarSubmenuItem link="servicing" submenu="servicing-modify-budgets" icon="clipboard outline" title="Modify Budgets"/>
                <SidebarSubmenuItem link="servicing" submenu="servicing-properties" icon="clipboard outline" title="Properties"/>
                <SidebarSubmenuItem link="servicing" submenu="servicing-modify-btb-budgets" icon="clipboard outline" title="Modify BTB Budgets"/>
                <SidebarSubmenuItem link="servicing" submenu="servicing-units" icon="clipboard outline" title="Units"/>
                <SidebarSubmenuItem link="servicing" submenu="servicing-btb-loans-for-approval" icon="clipboard outline" title="BTB Loans for P2 Approval"/>
                <SidebarSubmenuItem link="servicing" submenu="servicing-amendments" icon="clipboard outline" title="Amendments"/>
                <SidebarSubmenuItem link="servicing" submenu="servicing-insurance" icon="clipboard outline" title="Insurance"/>
                <SidebarSubmenuItem link="servicing" submenu="servicing-payoff-quotes" icon="clipboard outline" title="Payoff Quotes"/>
                <SidebarSubmenuItem link="servicing" submenu="servicing-payoff-transactions" icon="clipboard outline" title="Payoff Transactions"/>
                <SidebarSubmenuItem link="servicing" submenu="servicing-purchase-company-transactions" icon="clipboard outline" title="Purchase Company Transactions"/>
                <SidebarSubmenuItem link="servicing" submenu="servicing-purchase-advice-quote" icon="clipboard outline" title="Purchase Advice Quote"/>
                <SidebarSubmenuItem link="servicing" submenu="servicing-purchase-advice-transactions" icon="clipboard outline" title="Purchase Advice Transactions"/>
                <SidebarSubmenuItem link="servicing" submenu="servicing-close-accounting-month" icon="clipboard outline" title="Close Accounting Month"/>
                <SidebarSubmenuItem link="servicing" submenu="servicing-loan-history" icon="clipboard outline" title="Loan History"/>
                <SidebarSubmenuItem link="servicing" submenu="servicing-loan-sale-approvals" icon="clipboard outline" title="Loan Sale Approvals"/>
                <SidebarSubmenuItem link="servicing" submenu="servicing-inspection-company-messages" icon="clipboard outline" title="Inspection Company Messages"/>
                <SidebarSubmenuItem link="servicing" submenu="servicing-loan-conditions" icon="clipboard outline" title="Loan Conditions"/>
                <SidebarSubmenuItem link="servicing" submenu="servicing-current-inspections" icon="clipboard outline" title="Current Inspections"/>
                <SidebarSubmenuItem link="servicing" submenu="servicing-auto-po-restrictions" icon="clipboard outline" title="Auto PO Restrictions"/>
              </div>}
            </Transition.Group>
            <SidebarSidemenuContainer link="accounting" submenu="accounting-initiate-loan-costs" icon="table" title="Accounting"/>
            <Transition.Group
              animation={"fade down"}
              duration={500}
            >
              {isMenuExpanded("accounting") && <div>
                <SidebarSubmenuItem link="accounting" submenu="accounting-initiate-loan-costs" icon="clipboard outline" title="Initiate Loan Costs"/>
                <SidebarSubmenuItem link="accounting" submenu="accounting-loan-transactions" icon="clipboard outline" title="Loan Transactions"/>
                <SidebarSubmenuItem link="accounting" submenu="accounting-post-payments" icon="clipboard outline" title="Post Payments"/>
                <SidebarSubmenuItem link="accounting" submenu="accounting-loan-payments" icon="clipboard outline" title="Loan Payments"/>
                <SidebarSubmenuItem link="accounting" submenu="accounting-bank-routings" icon="clipboard outline" title="Bank Routing #'s"/>
                <SidebarSubmenuItem link="accounting" submenu="accounting-purchasing-company-payments" icon="clipboard outline" title="Purchasing Company Payments"/>
                <SidebarSubmenuItem link="accounting" submenu="accounting-purchasing-company-interest" icon="clipboard outline" title="Purchasing Company Interest"/>
                <SidebarSubmenuItem link="accounting" submenu="accounting-late-fee-transactions" icon="clipboard outline" title="Late Fee Transactions"/>
                <SidebarSubmenuItem link="accounting" submenu="accounting-loan-draws-and-transactions" icon="clipboard outline" title="Loan Draws And Transactions"/>
                <SidebarSubmenuItem link="accounting" submenu="accounting-unfunded-draws" icon="clipboard outline" title="UnFunded Draws" />
                <SidebarSubmenuItem link="accounting" submenu="accounting-draws-in-process" icon="clipboard outline" title="Draws In Process" />
                <SidebarSubmenuItem link="accounting" submenu="accounting-funded-draws" icon="clipboard outline" title="Funded Draws" />
                <SidebarSubmenuItem link="accounting" submenu="accounting-accounting-je" icon="clipboard outline" title="Accounting JE" />
                <SidebarSubmenuItem link="accounting" submenu="accounting-net-suit-je" icon="clipboard outline" title="Net Suit JE" />
                <SidebarSubmenuItem link="accounting" submenu="accounting-ach-paid" icon="clipboard outline" title="ACH Paid" />
                <SidebarSubmenuItem link="accounting" submenu="accounting-ach-queue" icon="clipboard outline" title="ACH Queue" />
                <SidebarSubmenuItem link="accounting" submenu="accounting-weekly-recalc-report" icon="clipboard outline" title="Weekly Recalc Report" />
                <SidebarSubmenuItem link="accounting" submenu="accounting-monthly-recalc-report" icon="clipboard outline" title="Monthly Recalc Report" />
                <SidebarSubmenuItem link="accounting" submenu="accounting-ir-details" icon="clipboard outline" title="IR Details" />
                <SidebarSubmenuItem link="accounting" submenu="accounting-residual-amounts-queue" icon="clipboard outline" title="Residual Amounts Queue" />
              </div>}
            </Transition.Group>
            <SidebarSidemenuContainer link="documents" icon="table" title="Documents" />
            <Transition.Group
              animation={"fade down"}
              duration={500}
            >
              {isMenuExpanded("documents") && <div>
                <SidebarSubmenuItem link="documents" submenu="documents" icon="clipboard outline" title="Documents" />
                <SidebarSubmenuItem link="documents" submenu="documents-upload-direct-pay-form" icon="clipboard outline" title="Upload Direct Pay Form" />
                <SidebarSubmenuItem link="documents" submenu="documents-upload-loan-data-cash-flow" icon="clipboard outline" title="Upload Loan Data Cash Flow" />
                <SidebarSubmenuItem link="documents" submenu="documents-docusign-status" icon="clipboard outline" title="DocuSign Status" />
                <SidebarSubmenuItem link="documents" submenu="documents-docusign-master" icon="clipboard outline" title="DocuSign Document Master" />
                <SidebarSubmenuItem link="documents" submenu="documents-docusign-sender-receiver" icon="clipboard outline" title="DocuSign Sender/Receiver" />
              </div>}
            </Transition.Group>
            <SidebarSidemenuContainer link="inspections" icon="table" submenu="inspections-submit-to-inspection-company" title="Inspections" />
            <Transition.Group
              animation={"fade down"}
              duration={500}
            >
              {isMenuExpanded("inspections") && <div>
                <SidebarSubmenuItem link="inspections" submenu="inspections-submit-to-inspection-company" icon="clipboard outline" title="Submit To Inspection Company"/>
                <SidebarSubmenuItem link="inspections" submenu="inspection-company-data-match" icon="clipboard outline" title="Inspection Company Data match" />
                <SidebarSubmenuItem link="inspections" submenu="inspection-company-pending-projects" icon="clipboard outline" title="Inspection Company Pending Projects"/>         
                <SidebarSubmenuItem link="inspections" submenu="inspections-inspection-company-projects" icon="clipboard outline" title="Inspection Company Projects"/>
                <SidebarSubmenuItem link="inspections" submenu="inspections-inspection-company-project-documents" icon="clipboard outline" title="Inspection Company Project Documents"/>
                <SidebarSubmenuItem link="inspections" submenu="inspections-inspection-company-inspections" icon="clipboard outline" title="Inspection Company Inspections"/>
                <SidebarSubmenuItem link="inspections" submenu="inspections-inspection-company-inspection-details" icon="clipboard outline" title="Inspection Company Inspection Details"/>
                <SidebarSubmenuItem link="inspections" submenu="inspections-inspection-company-inspection-documents" icon="clipboard outline" title="Inspection Company Inspection Documents"/>
                <SidebarSubmenuItem link="inspections" submenu="inspections-inspection-company-draw-funded" icon="clipboard outline" title="Inspection Company Draw Funded"/>   
                <SidebarSubmenuItem link="inspections" submenu="inspections-inspection-company-message-subject" icon="clipboard outline" title="Inspection Company Message Subject"/>
              </div>}
            </Transition.Group>
            <SidebarSidemenuContainer link="reports" submenu="reports-lms-reports" icon="table" title="Reports"/>
            <Transition.Group
              animation={"fade down"}
              duration={500}
            >
              {isMenuExpanded("reports") && <div>
                <SidebarSubmenuItem link="reports" submenu="reports-lms-reports" icon="clipboard outline" title="LMS Reports"/>
                <SidebarSubmenuItem link="reports" submenu="reports-borrower-statement-previous-owing" icon="clipboard outline" title="Borrower Statement Previous Owing"/>
                <SidebarSubmenuItem link="reports" submenu="reports-borrower-statement-current-interest-owing" icon="clipboard outline" title="Borrower Statement Current Interest Owing"/>
                <SidebarSubmenuItem link="reports" submenu="reports-ab-data-at-sale" icon="clipboard outline" title="A/B Data At Sale"/>
                <SidebarSubmenuItem link="reports" submenu="reports-ab-data-after-sale" icon="clipboard outline" title="A/B Data After Sale"/>
                <SidebarSubmenuItem link="reports" submenu="reports-waterfall-summary" icon="clipboard outline" title="Waterfall Summary"/>
                <SidebarSubmenuItem link="reports" submenu="reports-collections" icon="clipboard outline" title="Collections"/>
                <SidebarSubmenuItem link="reports" submenu="reports-wire-amounts" icon="clipboard outline" title="Wire Amounts"/>
                <SidebarSubmenuItem link="reports" submenu="reports-inspection-ready" icon="clipboard outline" title="Inspection Ready"/>
              </div>}
            </Transition.Group>
            <SidebarSidemenuContainer link="vendors" submenu="vendors-budget-categories" icon="table" title="Vendors"/>
            <Transition.Group
              animation={"fade down"}
              duration={500}
            >
              {isMenuExpanded("vendors") && <div>
                <SidebarSubmenuItem link="vendors" submenu="vendors-budget-categories" icon="clipboard outline" title="Budget Categories"/>
                <SidebarSubmenuItem link="vendors" submenu="vendors-products-services" icon="clipboard outline" title="Products/ Services"/>
                <SidebarSubmenuItem link="vendors" submenu="vendors-service-areas" icon="clipboard outline" title="Service Areas"/>
                <SidebarSubmenuItem link="vendors" submenu="vendors-bank-accounts" icon="clipboard outline" title="Bank Accounts"/>
                <SidebarSubmenuItem link="vendors" submenu="vendors-merge-vendors" icon="clipboard outline" title="Merge Vendors"/>                
              </div>}
            </Transition.Group>            
            <SidebarSidemenuContainer link="voting" submenu="voting-randomizer" icon="table" title="Voting" />
            <Transition.Group
              animation={"fade down"}
              duration={500}
            >
              {isMenuExpanded("voting") && <div>
                <SidebarSubmenuItem link="voting" submenu="voting-randomizer" icon="clipboard outline" title="Randomizer" />
                <SidebarSubmenuItem link="voting" submenu="voting-assign-fund" icon="clipboard outline" title="Assign Funds" />
                <SidebarSubmenuItem link="voting" submenu="voting-loan-review" icon="clipboard outline" title="Loan Review" />
                <SidebarSubmenuItem link="voting" submenu="voting-fund-polling" icon="clipboard outline" title="Fund Polling" />
                <SidebarSubmenuItem link="voting" submenu="voting-btb-p2-review" icon="clipboard outline" title="BTB P2 Review" />
                <SidebarSubmenuItem link="voting" submenu="voting-cac-members" icon="clipboard outline" title="CAC Members" />
                <SidebarSubmenuItem link="voting" submenu="voting-amendment-review" icon="clipboard outline" title="Amendment Review" />
              </div>}
            </Transition.Group>
            <SidebarSidemenuContainer link="systems" submenu="systems-users" icon="table" title="Systems" />
            <Transition.Group
              animation={"fade down"}
              duration={500}
            >
              {isMenuExpanded("systems") && <div>
                <SidebarSubmenuItem link="systems" submenu="systems-users" icon="clipboard outline" title="Users" />
                <SidebarSubmenuItem link="systems" submenu="systems-external-users" icon="clipboard outline" title="External Users" />
                <SidebarSubmenuItem link="systems" submenu="systems-locked-loans" icon="clipboard outline" title="Locked Loans" />
                <SidebarSubmenuItem link="systems" submenu="systems-loan-comments" icon="clipboard outline" title="Loan Comments" />
                <SidebarSubmenuItem link="systems" submenu="systems-related-parties" icon="clipboard outline" title="Related Parties" />
              </div>}
            </Transition.Group>
            <SidebarSidemenuContainer link="new-menus" submenu="new-menus-borrower-application-entity" icon="table" title="New Menus" />
            <Transition.Group
              animation={"fade down"}
              duration={500}
            >
              {isMenuExpanded("new-menus") && <div>
                <SidebarSubmenuItem link="new-menus" submenu="new-menus-borrower-application-entity" icon="clipboard outline" title="Borrower Application Entity" />
                <SidebarSubmenuItem link="new-menus" submenu="new-menus-borrower-vendor" icon="clipboard outline" title="Borrower Vendor" />
                <SidebarSubmenuItem link="new-menus" submenu="new-menus-loan-applications" icon="clipboard outline" title="Loan Applications" />
                <SidebarSubmenuItem link="new-menus" submenu="new-menus-email-box" icon="clipboard outline" title="Email Box" />
                <SidebarSubmenuItem link="new-menus" submenu="new-menus-email-master" icon="clipboard outline" title="Email Master" />
                <SidebarSubmenuItem link="new-menus" submenu="new-menus-email-tag" icon="clipboard outline" title="Email Tag" />
                <SidebarSubmenuItem link="new-menus" submenu="new-menus-gmr-project-cost" icon="clipboard outline" title="GMR Project Cost" />                  
                <SidebarSubmenuItem link="new-menus" submenu="new-menus-cross-collateral" icon="clipboard outline" title="Cross Collateral" />
                <SidebarSubmenuItem link="new-menus" submenu="new-menus-document-master-requirement" icon="clipboard outline" title="Document Master Requirement" />
                <SidebarSubmenuItem link="new-menus" submenu="new-menus-email-templates" icon="clipboard outline" title="Email Templates" />
                <SidebarSubmenuItem link="new-menus" submenu="new-menus-credit-request" icon="clipboard outline" title="Credit Request" />
                <SidebarSubmenuItem link="new-menus" submenu="new-menus-console-reports" icon="clipboard outline" title="Console Reports" />
                <SidebarSubmenuItem link="new-menus" submenu="new-menus-comment-subject" icon="clipboard outline" title="Comment Subject" />
                <SidebarSubmenuItem link="new-menus" submenu="new-menus-loan-draw-history" icon="clipboard outline" title="Loan Draw History" />
                <SidebarSubmenuItem link="new-menus" submenu="new-menus-loan-sub-status-email-config" icon="clipboard outline" title="Loan Sub Status Email Config" />
                <SidebarSubmenuItem link="new-menus" submenu="new-menus-loan-condition-default" icon="clipboard outline" title="Loan Condition Default" />
                <SidebarSubmenuItem link="new-menus" submenu="new-menus-loan-doc-generation" icon="clipboard outline" title="Loan Doc Generation" />
                <SidebarSubmenuItem link="new-menus" submenu="new-menus-loan-draw-batch" icon="clipboard outline" title="Loan Draw Batch" />
                <SidebarSubmenuItem link="new-menus" submenu="new-menus-invoice" icon="clipboard outline" title="Invoice" />
                <SidebarSubmenuItem link="new-menus" submenu="new-menus-legal-text" icon="clipboard outline" title="Legal Text" />
                <SidebarSubmenuItem link="new-menus" submenu="new-menus-validation-message" icon="clipboard outline" title="Validation Message" />
                <SidebarSubmenuItem link="new-menus" submenu="new-menus-notification" icon="clipboard outline" title="Notification" />
                </div>}
            </Transition.Group>
            <SidebarSidemenuContainer link="funds" submenu="bank-loc" icon="table" title="Funds" />
            <Transition.Group
              animation={"fade down"}
              duration={500}
            >
              {isMenuExpanded("funds") && <div>
                <SidebarSubmenuItem link="funds" submenu="bank-loc" icon="clipboard outline" title="Bank LOC" />
                <SidebarSubmenuItem link="funds" submenu="bank-loc-history" icon="clipboard outline" title="Bank LOC History" />
                <SidebarSubmenuItem link="funds" submenu="voting-members" icon="clipboard outline" title="Voting Members" />
                <SidebarSubmenuItem link="funds" submenu="fund-allocation" icon="clipboard outline" title="Fund Allocation" />
                <SidebarSubmenuItem link="funds" submenu="fund-capacity" icon="clipboard outline" title="Fund Capacity" />
                <SidebarSubmenuItem link="funds" submenu="fund-capacity-commitment" icon="clipboard outline" title="Fund Capacity Commitment" />
                <SidebarSubmenuItem link="funds" submenu="fund-managers" icon="clipboard outline" title="Fund Mangers" />
              </div>}
            </Transition.Group>
          </React.Fragment>}
        </Sidebar>
        <Sidebar.Pusher>
          <Segment className="sidebar-body-container" basic>
            {children}
          </Segment>
        </Sidebar.Pusher>
      </Sidebar.Pushable>
    </div>
  );
};

export default BCSidebar;